import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, money, resolveImg, QUOTE_STATUS, computeProfit } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Pencil, History, Loader2, ChevronDown, FileText, User, Package, Eye, CheckCircle2, AlertTriangle, TrendingUp, Trash2 } from "lucide-react";

function noteLines(note) {
  return (note || "").split("\n").map((l) => l.trim()).filter(Boolean);
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user, isAdmin } = useAuth();
  const canProfit = can("view_profit");
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [mailOpen, setMailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [mail, setMail] = useState({ recipient_email: "", subject: "", body: "", lang: "tr" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([api.get(`/quotes/${id}`), api.get("/settings")]);
      setQuote(q.data); setSettings(s.data);
    } catch { navigate("/teklifler"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (lang) => {
    setDownloading(true);
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${quote.quote_number}-${lang}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF indirildi (${lang.toUpperCase()})`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setDownloading(false); }
  };

  const previewPdf = async (lang) => {
    setPreviewLoading(true); setPreviewOpen(true); setPreviewUrl("");
    try {
      const res = await api.get(`/quotes/${id}/pdf`, { params: { lang }, responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch (err) { toast.error(apiError(err)); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      setQuote(data);
      toast.success("Durum güncellendi");
    } catch (err) { toast.error(apiError(err)); }
  };

  const openMail = () => {
    const cust = quote.customer_snapshot || {};
    const tpl = settings.email || {};
    const custName = cust.company_name || cust.name || quote.customer_name || "";
    setMail({
      recipient_email: cust.email || "",
      subject: (tpl.subject_template || "Teklifimiz - {quote_no}").replace("{quote_no}", quote.quote_number),
      body: (tpl.body_template || "Sayın {customer},\n\n{quote_no} numaralı teklifimiz ektedir.")
        .replace(/{customer}/g, custName).replace(/{quote_no}/g, quote.quote_number),
      lang: "tr",
    });
    setMailOpen(true);
  };

  const sendMail = async () => {
    if (!mail.recipient_email) { toast.error("Alıcı e-posta girin"); return; }
    setSending(true);
    try {
      await api.post(`/quotes/${id}/email`, mail);
      toast.success("Teklif e-posta ile gönderildi");
      setMailOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const openRevisions = async () => {
    try { const { data } = await api.get(`/quotes/${id}/revisions`); setRevisions(data); setRevOpen(true); }
    catch (err) { toast.error(apiError(err)); }
  };

  const removeQuote = async () => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Teklif silindi");
      navigate("/teklifler");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  if (!quote) return null;
  const cust = quote.customer_snapshot || {};
  const hasVat = quote.vat_rate !== null && quote.vat_rate !== undefined;
  const st = QUOTE_STATUS[quote.status] || QUOTE_STATUS.draft;
  const isOwner = quote.prepared_by === user?.id;
  const canEditQuote = isAdmin || isOwner || can("quote_edit_any");
  const canDeleteQuote = isAdmin || isOwner || can("quote_delete_any");
  const canStatusQuote = isAdmin || isOwner || can("quote_status_any");
  const canEmailQuote = isAdmin || isOwner || can("quote_email_any");

  return (
    <div>
      <Button variant="ghost" className="mb-4 -ml-2 rounded-xl" onClick={() => navigate("/teklifler")} data-testid="qd-back"><ArrowLeft className="w-4 h-4 mr-2" /> Teklifler</Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{quote.quote_number}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${st.cls}`} data-testid="qd-status-badge">{st.label}</span>
            {quote.revision > 0 && <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 text-slate-500">Rev. {quote.revision}</span>}
          </div>
          <p className="text-sm text-slate-500 mt-1">{(quote.created_at || "").slice(0, 10)} · Hazırlayan: <span className="font-medium text-slate-700">{quote.prepared_by_name}</span></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canStatusQuote && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-xl" data-testid="qd-status"><CheckCircle2 className="w-4 h-4 mr-2" />Durum<ChevronDown className="w-4 h-4 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => changeStatus("draft")} data-testid="qd-status-draft">Taslak</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeStatus("sent")} data-testid="qd-status-sent">Gönderildi</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeStatus("approved")} data-testid="qd-status-approved" className="text-emerald-700">Onaylandı</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeStatus("rejected")} data-testid="qd-status-rejected" className="text-rose-600">Reddedildi</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {canEditQuote && (
            <Button variant="outline" className="rounded-xl" onClick={() => navigate(`/teklifler/${id}/duzenle`)} data-testid="qd-edit"><Pencil className="w-4 h-4 mr-2" />Düzenle</Button>
          )}
          <Button variant="outline" className="rounded-xl" onClick={openRevisions} data-testid="qd-revisions"><History className="w-4 h-4 mr-2" />Geçmiş ({quote.revision || 0})</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-xl" disabled={downloading} data-testid="qd-pdf">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" />PDF<ChevronDown className="w-4 h-4 ml-1" /></>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => previewPdf("tr")} data-testid="qd-preview-tr"><Eye className="w-4 h-4 mr-2" />Önizle (Türkçe)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => previewPdf("en")} data-testid="qd-preview-en"><Eye className="w-4 h-4 mr-2" />Önizle (İngilizce)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadPdf("tr")} data-testid="qd-pdf-tr"><Download className="w-4 h-4 mr-2" />İndir 🇹🇷 Türkçe</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadPdf("en")} data-testid="qd-pdf-en"><Download className="w-4 h-4 mr-2" />İndir 🇬🇧 İngilizce (AI)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canEmailQuote && (
          <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" onClick={openMail} data-testid="qd-mail"><Mail className="w-4 h-4 mr-2" />Mail Gönder</Button>
          )}
          {canDeleteQuote && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700" data-testid="qd-delete"><Trash2 className="w-4 h-4 mr-2" />Sil</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Teklifi sil?</AlertDialogTitle>
                  <AlertDialogDescription>{quote.quote_number} numaralı teklif kalıcı olarak silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                  <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={removeQuote} data-testid="qd-delete-confirm">Sil</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border-slate-100 rounded-2xl card-soft lg:col-span-1">
          <p className="text-xs font-bold uppercase tracking-widest text-rose-600 mb-3 flex items-center gap-2"><User className="w-4 h-4" />Müşteri</p>
          <p className="font-heading font-bold text-lg">{cust.company_name || cust.name || quote.customer_name}</p>
          {cust.authorized_name && <p className="text-sm text-slate-600 mt-1">{cust.authorized_name}</p>}
          {cust.phone && <p className="text-sm text-slate-600 mt-2">{cust.phone}</p>}
          {cust.email && <p className="text-sm text-slate-600">{cust.email}</p>}
          {cust.tax_number && <p className="text-xs text-slate-400 mt-2">VN: {cust.tax_number} · {cust.tax_office}</p>}
          {cust.address && <p className="text-sm text-slate-600 mt-2">{cust.address}</p>}
        </Card>

        <Card className="p-6 border-slate-100 rounded-2xl card-soft lg:col-span-2">
          <div className="space-y-3">
            {quote.items.map((it, idx) => (
              <div key={idx} className="flex justify-between gap-4 pb-3 border-b border-slate-100 last:border-0">
                <div className="flex gap-3 min-w-0">
                  <div className="w-14 h-14 rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                    {it.image_url ? <img src={resolveImg(it.image_url)} alt={it.name} className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{it.name} {it.stock_code && <span className="text-slate-400 font-normal">({it.stock_code})</span>}</p>
                    {it.description && <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>}
                    {noteLines(it.note).length > 0 && (
                      <ul className="text-xs text-rose-600 italic mt-1 list-disc list-inside space-y-0.5">
                        {noteLines(it.note).map((l, li) => <li key={li}>{l}</li>)}
                      </ul>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{it.quantity} x {money(it.unit_price, quote.currency)} {it.discount_percent > 0 && `· -%${it.discount_percent}`}</p>
                    {it.below_cost && (
                      <p className="text-[11px] mt-1 inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5" data-testid={`qd-belowcost-${idx}`}>
                        <AlertTriangle className="w-3 h-3" /> Alış fiyatı altında{it.below_cost_approved_by ? ` · Onaylayan: ${it.below_cost_approved_by}` : ""}{it.below_cost_approved_at ? ` · ${(it.below_cost_approved_at || "").slice(0, 10)}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <p className="font-heading font-bold text-sm whitespace-nowrap">{money((it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount_percent || 0) / 100), quote.currency)}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-200 space-y-2 text-sm max-w-xs ml-auto">
            {hasVat ? (
              <>
                <div className="flex justify-between"><span className="text-slate-500">Ara Toplam (KDV Hariç)</span><span>{money(quote.subtotal, quote.currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">KDV (%{quote.vat_rate})</span><span>{money(quote.vat_amount, quote.currency)}</span></div>
                <div className="flex justify-between font-medium"><span className="text-slate-600">Ara Toplam (KDV Dahil)</span><span>{money(quote.gross_subtotal ?? (quote.subtotal + quote.vat_amount), quote.currency)}</span></div>
                {quote.total_discount_amount > 0 && <div className="flex justify-between text-rose-600"><span>Genel İskonto (KDV dahil)</span><span>- {money(quote.total_discount_amount, quote.currency)}</span></div>}
              </>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-slate-500">Ara Toplam</span><span>{money(quote.subtotal, quote.currency)}</span></div>
                {quote.total_discount_amount > 0 && <div className="flex justify-between text-rose-600"><span>Genel İskonto</span><span>- {money(quote.total_discount_amount, quote.currency)}</span></div>}
                <div className="text-xs text-slate-400 italic">Fiyatlara KDV dahildir.</div>
              </>
            )}
            <div className="flex justify-between items-center bg-slate-900 text-white rounded-xl px-4 py-2.5 mt-2">
              <span className="font-bold text-xs">GENEL TOPLAM</span>
              <span className="font-heading font-black">{money(quote.grand_total, quote.currency)}</span>
            </div>
          </div>
        </Card>
      </div>

      {canProfit && (() => {
        const p = computeProfit(quote);
        return (
          <Card className="p-6 border-slate-100 rounded-2xl card-soft mt-6" data-testid="qd-profit-card">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-600 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Kârlılık (Dahili)</p>
            {p.calculable ? (
              <div className="flex flex-wrap gap-x-10 gap-y-3">
                <div><p className="text-xs text-slate-400">Toplam Maliyet</p><p className="font-heading font-bold text-lg">{money(p.cost, quote.currency)}</p></div>
                <div><p className="text-xs text-slate-400">Kâr</p><p className={`font-heading font-bold text-lg ${p.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="qd-profit-amount">{money(p.profit, quote.currency)}</p></div>
                <div><p className="text-xs text-slate-400">Kâr Oranı (maliyet üzeri)</p><p className={`font-heading font-bold text-lg ${p.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="qd-profit-rate">%{p.rate.toFixed(1)}</p></div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm" data-testid="qd-profit-warning">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Teklifinizde alış / satış fiyatı belli olmayan ürünler var. Kâr miktarı ve kâr oranı hesaplanamadı.</span>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">Bu bilgi yalnızca dahili görünürdür; PDF'te ve müşteriye gönderilen teklifte yer almaz.</p>
          </Card>
        );
      })()}

      {/* Revisions dialog */}
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Revizyon Geçmişi</DialogTitle></DialogHeader>
          {revisions.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Henüz revizyon yok. Teklif düzenlendiğinde önceki hali burada saklanır.</p>
          ) : (
            <div className="space-y-3">
              {revisions.slice().reverse().map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4" data-testid={`revision-${r.revision}`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 text-slate-600">Revizyon {r.revision}</span>
                    <span className="text-xs text-slate-400">{(r.revised_at || "").slice(0, 16).replace("T", " ")} · {r.revised_by_name}</span>
                  </div>
                  <div className="space-y-2">
                    {(r.items || []).map((it, k) => (
                      <div key={k} className="text-xs bg-slate-50 rounded-lg p-2.5">
                        <div className="flex justify-between font-medium text-slate-700">
                          <span>{it.name} {it.stock_code && <span className="text-slate-400">({it.stock_code})</span>}</span>
                          <span>{money((it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount_percent || 0) / 100), r.currency)}</span>
                        </div>
                        <p className="text-slate-400 mt-0.5">{it.quantity} x {money(it.unit_price, r.currency)}{it.discount_percent > 0 ? ` · -%${it.discount_percent}` : ""}</p>
                        {it.description && <p className="text-slate-500 mt-0.5">{it.description}</p>}
                        {noteLines(it.note).map((l, li) => <p key={li} className="text-rose-600 italic">• {l}</p>)}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 text-xs space-y-1">
                    <div className="flex justify-between text-slate-500"><span>Para Birimi / KDV</span><span>{r.currency} · {r.vat_rate != null ? `%${r.vat_rate}` : "KDV dahil"}</span></div>
                    {r.total_discount_percent > 0 && <div className="flex justify-between text-rose-600"><span>Genel İskonto</span><span>%{r.total_discount_percent} (- {money(r.total_discount_amount, r.currency)})</span></div>}
                    {r.valid_until && <div className="flex justify-between text-slate-500"><span>Geçerlilik</span><span>{r.valid_until}</span></div>}
                    <div className="flex justify-between font-bold text-slate-800"><span>Genel Toplam</span><span>{money(r.grand_total, r.currency)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF preview dialog */}
      <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); } }}>
        <DialogContent className="max-w-4xl h-[88vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5"><DialogTitle className="font-heading">PDF Önizleme</DialogTitle><DialogDescription>Teklifin baskı önizlemesi. Göndermeden önce kontrol edebilirsiniz.</DialogDescription></DialogHeader>
          <div className="flex-1 min-h-0 px-6 pb-6">
            {previewLoading || !previewUrl ? (
              <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>
            ) : (
              <iframe src={previewUrl} title="PDF Önizleme" className="w-full h-full rounded-xl border border-slate-200" data-testid="pdf-preview-frame" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mail dialog */}
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Teklifi E-posta ile Gönder</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Alıcı E-posta</Label><Input value={mail.recipient_email} onChange={(e) => setMail((m) => ({ ...m, recipient_email: e.target.value }))} data-testid="mail-recipient" /></div>
            <div className="space-y-2"><Label>PDF Dili</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={mail.lang === "tr" ? "default" : "outline"} className={`rounded-xl ${mail.lang === "tr" ? "bg-slate-900" : ""}`} onClick={() => setMail((m) => ({ ...m, lang: "tr" }))} data-testid="mail-lang-tr">🇹🇷 Türkçe</Button>
                <Button type="button" size="sm" variant={mail.lang === "en" ? "default" : "outline"} className={`rounded-xl ${mail.lang === "en" ? "bg-slate-900" : ""}`} onClick={() => setMail((m) => ({ ...m, lang: "en" }))} data-testid="mail-lang-en">🇬🇧 İngilizce</Button>
                <Button type="button" size="sm" variant="outline" className="rounded-xl ml-auto" onClick={() => previewPdf(mail.lang)} data-testid="mail-preview"><Eye className="w-4 h-4 mr-2" />PDF Önizle</Button>
              </div>
            </div>
            <div className="space-y-2"><Label>Konu</Label><Input value={mail.subject} onChange={(e) => setMail((m) => ({ ...m, subject: e.target.value }))} data-testid="mail-subject" /></div>
            <div className="space-y-2"><Label>Mesaj</Label><Textarea value={mail.body} onChange={(e) => setMail((m) => ({ ...m, body: e.target.value }))} rows={7} data-testid="mail-body" /></div>
            <p className="text-xs text-slate-400"><FileText className="w-3 h-3 inline mr-1" />Teklif PDF'i şık bir kurumsal şablonla, ek olarak ve indirme bağlantısıyla gönderilir.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setMailOpen(false)}>Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" onClick={sendMail} disabled={sending} data-testid="mail-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" />Gönder</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
